import type { ProjectMetrics } from "../src/shared/summary.ts";
import type { CapacitySnap, ProjectSnap, RefEventSnap, RunSnap, SourceError, WorkSnap } from "../src/shared/types.ts";

export interface Freshness {
  generated_at: string;
  received_at: string;
  age_seconds: number;
  stale: boolean;
  stale_after_seconds: number;
}

export interface NeedsItem {
  kind: "approval" | "decision" | "attention" | "parked";
  project_id: string | null;
  id: string;
  at: string | null;
  text: string;
  detail?: string[];
}

export interface OverviewProject {
  id: string;
  name: string;
  listed: boolean;
  environment_kind: string | null;
  metrics: ProjectMetrics;
  summary: string;
  recent_refs: RefEventSnap[];
}

export interface Overview {
  empty?: true;
  freshness: Freshness;
  summary: string;
  needs_you: NeedsItem[];
  projects: OverviewProject[];
  capacity: CapacitySnap | null;
  errors: SourceError[];
  exporter: { version: string };
}

export interface ProjectDetail {
  freshness: Freshness;
  project: ProjectSnap;
  metrics: ProjectMetrics;
  summary: string;
  needs_you: NeedsItem[];
  runs: RunSnap[];
  work: WorkSnap[];
  ref_events: RefEventSnap[];
  timeline: { ts: number; kind: string; text: string; ref: string | null }[];
  series: { ts: number; runs_in_flight: number; runs_total: number; work_total: number; work_done: number; needs_you: number }[];
}

export interface AuthState {
  authenticated: boolean;
  enrolled: boolean;
  setup_available: boolean;
}

export class HttpError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string) {
    super(code);
    this.status = status;
    this.code = code;
  }
}

export interface Fetched<T> {
  data: T;
  offline: boolean;
}

export async function getJson<T>(path: string): Promise<Fetched<T>> {
  const res = await fetch(path, { credentials: "same-origin", headers: { accept: "application/json" } });
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new HttpError(res.status, body.error ?? "error");
  return { data: body, offline: res.headers.get("x-sbd-offline") === "1" };
}

export async function postJson<T>(path: string, payload: unknown = {}, method = "POST"): Promise<T> {
  const res = await fetch(path, { method, credentials: "same-origin", headers: { "content-type": "application/json" }, body: method === "DELETE" ? undefined : JSON.stringify(payload) });
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new HttpError(res.status, body.error ?? "error");
  return body;
}
